import React, { useContext, useState } from "react";
import moment from "moment";
import { Dialog, Grid, Typography } from "@material-ui/core";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import EditIcon from "@material-ui/icons/Edit";
import {
  UserDialogActions,
  UserDialogContent,
  UserDialogTitle,
  useUserDialogStyle,
} from "../../commons/dialog/UserDialog";
import { CurrentUserContext } from "../../commons/currentuser/CurrentUserContext";
import BookingView from "./BookingView";
import { Booking } from "../booking.model";
import CancelBookingButton from "../../commons/buttons/CancelBookingButton";
import UpdateBookingButton from "../../commons/buttons/UpdateBookingButton";
import RemoveBookingDialog, { RemoveAction } from "./RemoveBookingDialog";
import { ROLES } from "../../../_helpers";
import BookingUpdateDialog from "./BookingUpdateDialog";
import FinishBookingButton from "../../commons/buttons/FinishBookingButton";
import { TimerOff } from "@material-ui/icons";
import { useCurrentUser } from "../../commons/currentuser/CurrentUserHook";
import { BookingUpdateAttendeesDialog } from "./BookingUpdateAttendeesDialog";
import { ListedUser } from "../../users/listedUser.model";

type Props = {
  open: boolean;
  bookingInfo: Booking;
  onlyCancelYourOwn?: boolean;
  onSave: () => void;
  onFinishBooking: (bookingId: string) => void;
  onUpdateBooking: (booking: Booking) => void;
  onCancelBooking: () => void;
  onUpdateAttendees?: (bookingId: string, attendees: ListedUser[]) => void;
  onUpdateAttendeesRecurrent?: (
    bookingId: string,
    attendees: ListedUser[],
    from?: Date,
  ) => void;
  onCancelRecurrentBooking: (bookingId: string, from?: Date) => void;
  onRejectInvitation: () => void;
  onRejectRecurrentInvitation: (bookingId: string, from?: Date) => void;
  onClose: () => void;
};

const BookingViewDialog: React.FC<Props> = ({
  open,
  bookingInfo,
  onlyCancelYourOwn,
  onSave,
  onFinishBooking,
  onUpdateBooking,
  onCancelBooking,
  onCancelRecurrentBooking,
  onUpdateAttendees,
  onUpdateAttendeesRecurrent,
  onRejectInvitation,
  onRejectRecurrentInvitation,
  onClose,
}) => {
  const dialogClasses = useUserDialogStyle();
  const [openRemoveDialog, setOpenRemoveDialog] = useState<boolean>(false);
  const [openRejectInvitationDialog, setOpenRejectInvitationDialog] =
    useState<boolean>(false);
  const [openChangeAttendeesTypeDialog, setOpenChangeAttendeesTypeDialog] =
    useState<boolean>(false);
  const [openChangeAttendeesDialog, setOpenChangeAttendeesDialog] =
    useState<boolean>(false);
  const [changeAttendeeType, setChangeAttendeeType] = useState<string>();
  const [openUpdateDialog, setOpenUpdateDialog] = useState<boolean>(false);
  const { user } = useCurrentUser();

  const handleOpenUpdateDialog = () => {
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
  };

  const handleOnSave = () => {
    onSave();
    setOpenUpdateDialog(false);
  };

  const finishBooking = () => {
    onFinishBooking(bookingInfo.id);
    onClose();
  };

  const cancelBooking = () =>
    bookingInfo.isRecurrent ? setOpenRemoveDialog(true) : onCancelBooking();

  const handleCloseRemoveDialog = (action: RemoveAction) => {
    setOpenRemoveDialog(false);
    switch (action) {
      case "current":
        onCancelBooking();
        break;
      case "from":
        onCancelRecurrentBooking(bookingInfo.id, bookingInfo.bookingStart);
        break;
      case "all":
        onCancelRecurrentBooking(bookingInfo.id);
        break;
    }
  };

  const rejectBookingInvitation = () =>
    bookingInfo.isRecurrent
      ? setOpenRejectInvitationDialog(true)
      : onRejectInvitation();

  const handleCloseRejectInvitationDialog = (action: RemoveAction) => {
    setOpenRejectInvitationDialog(false);
    switch (action) {
      case "current":
        onRejectInvitation();
        break;
      case "from":
        onRejectRecurrentInvitation(bookingInfo.id, bookingInfo.bookingStart);
        break;
      case "all":
        onRejectRecurrentInvitation(bookingInfo.id);
        break;
    }
  };

  const changeAttendees = () => {
    if (!onUpdateAttendees) {
      return;
    }
    if (bookingInfo.isRecurrent) {
      setOpenChangeAttendeesTypeDialog(true);
    } else {
      setOpenChangeAttendeesDialog(true);
    }
  };

  const handleCloseChangeAttendeesTypeDialog = (action: RemoveAction) => {
    setOpenChangeAttendeesTypeDialog(false);
    if (["current", "from", "all"].includes(action)) {
      setOpenChangeAttendeesDialog(true);
      setChangeAttendeeType(action);
    }
  };

  const handleCloseChangeAttendeesDialog = () => {
    setChangeAttendeeType(undefined);
    setOpenChangeAttendeesDialog(false);
  };

  const handleSaveAttendees = (attendees: ListedUser[]) => {
    switch (changeAttendeeType) {
      case "current":
        onUpdateAttendees(bookingInfo.id, attendees ?? []);
        break;
      case "from":
        onUpdateAttendeesRecurrent(
          bookingInfo.id,
          attendees ?? [],
          bookingInfo.bookingStart,
        );
        break;
      case "all":
        onUpdateAttendeesRecurrent(bookingInfo.id, attendees ?? []);
        break;
      default:
        onUpdateAttendees(bookingInfo.id, attendees ?? []);
    }
    handleCloseChangeAttendeesDialog();
  };

  const isOwner = () => bookingInfo?.user === user.id;
  const isInvitee = () => bookingInfo?.attendees?.includes(user.id);

  const userDialogActions = () => {
    if (!bookingInfo) return null;
    const bookingIsComing = moment(bookingInfo.bookingStart).isAfter(moment());
    const bookingIsHappening = moment().isBetween(
      moment(bookingInfo.bookingStart),
      moment(bookingInfo.bookingEnd),
    );
    const canEditOrCancel =
      bookingIsComing &&
      (isOwner() || user.role === ROLES.assistant || !onlyCancelYourOwn);
    const canFinish =
      bookingIsHappening &&
      (isOwner() || user.role === ROLES.assistant || !onlyCancelYourOwn);
    const canRejectInvitation = isInvitee() && bookingIsComing;

    if (canEditOrCancel) {
      return (
        <UserDialogActions>
          <CancelBookingButton
            startIcon={<EventBusyIcon />}
            onClick={cancelBooking}
          >
            Cancelar
          </CancelBookingButton>
          <UpdateBookingButton
            startIcon={<EditIcon />}
            onClick={handleOpenUpdateDialog}
          >
            Editar
          </UpdateBookingButton>
        </UserDialogActions>
      );
    }

    if (canFinish) {
      return (
        <UserDialogActions>
          <FinishBookingButton startIcon={<TimerOff />} onClick={finishBooking}>
            Finalizar
          </FinishBookingButton>
        </UserDialogActions>
      );
    }

    if (canRejectInvitation) {
      return (
        <UserDialogActions>
          <CancelBookingButton
            startIcon={<EventBusyIcon />}
            onClick={rejectBookingInvitation}
          >
            Rechazar
          </CancelBookingButton>
        </UserDialogActions>
      );
    }
  };

  return (
    <>
      <Dialog
        classes={dialogClasses}
        onClose={onClose}
        aria-labelledby="booking-view-dialog"
        open={open}
        maxWidth={"sm"}
        fullWidth={true}
      >
        <UserDialogTitle
          id="booking-view-dialog"
          onClose={onClose}
          background={isOwner() ? "owner" : "external"}
        >
          <Grid container justifyContent="flex-end">
            <Grid item xs={11}>
              <Typography variant="h5">
                {bookingInfo?.title || "Reservado"}
              </Typography>
            </Grid>
          </Grid>
        </UserDialogTitle>
        <UserDialogContent dividers>
          <BookingView
            booking={bookingInfo}
            onEditAttendees={isOwner() ? changeAttendees : undefined}
          />
        </UserDialogContent>
        {userDialogActions()}
      </Dialog>
      <RemoveBookingDialog
        open={openRemoveDialog}
        onClose={handleCloseRemoveDialog}
      />
      <RemoveBookingDialog
        title="¿A que reservas no vas a asistir?"
        open={openRejectInvitationDialog}
        onClose={handleCloseRejectInvitationDialog}
      />

      <BookingUpdateDialog
        open={openUpdateDialog}
        booking={bookingInfo}
        onClose={handleCloseUpdateDialog}
        onSave={handleOnSave}
        onUpdateBooking={onUpdateBooking}
      />
      <RemoveBookingDialog
        title="¿A que reservas vas a actualizar los invitados?"
        open={openChangeAttendeesTypeDialog}
        onClose={handleCloseChangeAttendeesTypeDialog}
      />
      <BookingUpdateAttendeesDialog
        open={openChangeAttendeesDialog}
        attendees={bookingInfo?.attendeesInfo ?? []}
        onClose={handleCloseChangeAttendeesDialog}
        onSave={handleSaveAttendees}
      />
    </>
  );
};

export default BookingViewDialog;
